/**
 * Recomendaciones dinámicas del carrito lateral.
 *
 * Reemplaza los productos servidos desde una colección fija por los
 * recomendados de Shopify para el último producto agregado al carrito.
 *
 * Fallback: si las recomendaciones dinámicas están apagadas, si la API no
 * responde o si no quedan productos válidos, se conserva el listado
 * renderizado por Liquid desde `settings.collection_recommendations`.
 *
 * Config: `window.wayuCartRecos` (ver snippets/right-drawer.liquid).
 */
(function () {
  var cfg = window.wayuCartRecos || {};
  if (!cfg.enabled) return;

  // El endpoint de recomendaciones no entrega más de 10 productos.
  var API_MAX = 10;
  var LIMIT = Math.min(parseInt(cfg.limit, 10) || 6, API_MAX);
  var ROOT = cfg.root && cfg.root !== '/' ? cfg.root.replace(/\/$/, '') : '';
  var MONEY_FORMAT = cfg.moneyFormat || '${{amount}}';
  var EXCLUDED_TITLES = cfg.excludedTitles || ['Bolsa Wayu'];

  var fallbackHTML = new WeakMap();
  var painted = new WeakMap();
  // Contenedores cuyo markup de Liquid ya fue reemplazado alguna vez.
  var touched = new WeakSet();
  var cache = { productId: null, html: null };
  // Sube en cada recalculo de la lista. Vue destruye y recrea los nodos del
  // drawer, asi que el WeakMap se vacia solo y esos nodos se repintan.
  var renderToken = 0;
  var inFlight = false;
  var pendingCart = null;
  var syncTimer = null;
  var syncDeadline = 0;
  var SYNC_DEBOUNCE_MS = 50;
  var SYNC_MAX_MS = 300;
  var resolvedOnce = false;

  // Cantidades por producto del carrito anterior, para detectar por diferencia
  // cual entro. Se siembra desde Liquid: en el primer agregado ya hay con que
  // comparar, sin gastar un request al cargar la pagina.
  var prevCounts = null;
  var lastAddedProductId = null;

  var STORAGE_KEY = 'wayu:last-added-product';

  // Debe coincidir con la transicion de .reco-list--leaving en el CSS.
  var LEAVE_MS = 160;

  /* ------------------------------------------------------------------ utils */

  function formatMoney(cents) {
    var value = (cents || 0) / 100;

    function withSeparators(number, decimals, thousands, decimal) {
      var fixed = number.toFixed(decimals);
      var parts = fixed.split('.');
      var intPart = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      return decimals && parts[1] ? intPart + decimal + parts[1] : intPart;
    }

    return MONEY_FORMAT.replace(/\{\{\s*(\w+)\s*\}\}/g, function (_, name) {
      switch (name) {
        case 'amount':
          return withSeparators(value, 2, ',', '.');
        case 'amount_no_decimals':
          return withSeparators(value, 0, ',', '.');
        case 'amount_with_comma_separator':
          return withSeparators(value, 2, '.', ',');
        case 'amount_no_decimals_with_comma_separator':
          return withSeparators(value, 0, '.', ',');
        case 'amount_with_apostrophe_separator':
          return withSeparators(value, 2, "'", '.');
        case 'amount_with_space_separator':
          return withSeparators(value, 2, ' ', ',');
        case 'amount_no_decimals_with_space_separator':
          return withSeparators(value, 0, ' ', ',');
        default:
          return withSeparators(value, 2, ',', '.');
      }
    });
  }

  function escapeHTML(str) {
    return String(str == null ? '' : str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  function sizedImage(url, width) {
    if (!url) return '';
    return url.indexOf('?') > -1
      ? url + '&width=' + width
      : url + '?width=' + width;
  }

  function isExcluded(title) {
    return EXCLUDED_TITLES.indexOf(title) !== -1;
  }

  /* --------------------------------------------------------------- markup */
  /* Espeja snippets/recommendation.liquid con lateral: true */

  function recommendationHTML(product) {
    var variant =
      product.variants.filter(function (v) { return v.available; })[0] ||
      product.variants[0];
    if (!variant) return '';

    var onSale = product.compare_at_price > product.price;
    var priceClasses =
      'price' +
      (product.available ? '' : ' price--sold-out') +
      (onSale ? ' price--on-sale' : '');

    var priceInner = onSale
      ? '<span><s class="price-item price-item--regular">' +
        formatMoney(product.compare_at_price) +
        '</s></span>' +
        '<span class="price-item price-item--sale price-item--last price-item--sale-with-compare">' +
        formatMoney(product.price) +
        '</span>'
      : '<span class="price-item price-item--regular"></span>' +
        '<span class="price-item price-item--sale price-item--last">' +
        formatMoney(product.price) +
        '</span>';

    var url = ROOT + product.url;
    var image = sizedImage(product.featured_image, 120);

    return (
      '<div class="reco-container" data-reco-dynamic>' +
      '<div class="reco-img">' +
      '<a href="' + escapeHTML(url) + '" class="cart-item__link" tabindex="-1" aria-hidden="true">' +
      (image
        ? '<img class="cart-item__image" src="' + escapeHTML(image) +
          '" alt="' + escapeHTML(product.title) +
          '" loading="lazy" width="auto" height="auto">'
        : '') +
      '</a>' +
      '</div>' +
      '<div class="reco-info">' +
      '<a href="' + escapeHTML(url) + '" class="reco-p-title">' + escapeHTML(product.title) + '</a>' +
      '<span class="reco-price">' +
      '<div class="' + priceClasses + '"><div class="price__container">' +
      '<div class="price__sale">' + priceInner + '</div>' +
      '</div></div>' +
      '</span>' +
      '<button type="button" class="reco-add-btn" onclick="addToCart(' + variant.id + ', event);">' +
      '<span class="reco-add">AGREGAR AL CARRO</span>' +
      '</button>' +
      '</div>' +
      '</div>'
    );
  }

  /* ------------------------------------------------------------------ data */

  function getCart() {
    return fetch(ROOT + '/cart.js', { credentials: 'same-origin' }).then(function (r) {
      return r.json();
    });
  }

  /** Cantidades por producto, ignorando las bolsas. */
  function productCounts(items) {
    var counts = {};
    (items || []).forEach(function (item) {
      if (isExcluded(item.product_title)) return;
      counts[item.product_id] = (counts[item.product_id] || 0) + item.quantity;
    });
    return counts;
  }

  function readStoredSource() {
    try {
      var raw = window.sessionStorage.getItem(STORAGE_KEY);
      return raw ? parseInt(raw, 10) : null;
    } catch (e) {
      return null;
    }
  }

  function storeSource(productId) {
    try {
      window.sessionStorage.setItem(STORAGE_KEY, String(productId));
    } catch (e) {
      /* modo privado: seguimos sin persistir */
    }
  }

  /**
   * Producto que sembró las recomendaciones: el último que entró al carrito.
   *
   * Se resuelve comparando contra el carrito anterior en vez de asumir en qué
   * punta del arreglo Shopify deja los items nuevos. Un producto que aparece,
   * o que sube de cantidad, es el que se acaba de agregar. Las bolsas quedan
   * fuera del cálculo.
   */
  function sourceProductId(cart) {
    var counts = productCounts(cart.items);
    var ids = Object.keys(counts);

    if (!ids.length) {
      prevCounts = counts;
      return null;
    }

    if (prevCounts) {
      var added = null;

      // Un producto que antes no estaba gana sobre uno que solo subió cantidad.
      ids.forEach(function (id) {
        if (!prevCounts[id]) added = id;
      });
      if (added === null) {
        ids.forEach(function (id) {
          if (counts[id] > (prevCounts[id] || 0)) added = id;
        });
      }

      if (added !== null) {
        lastAddedProductId = parseInt(added, 10);
        storeSource(lastAddedProductId);
      }
    }

    prevCounts = counts;

    // Primera carga de la página: recuperamos el último agregado de la sesión.
    if (lastAddedProductId === null) lastAddedProductId = readStoredSource();

    // Si no hay dato previo, o el producto ya no está en el carrito, se usa el
    // último item como aproximación.
    if (lastAddedProductId === null || !counts[lastAddedProductId]) {
      var items = (cart.items || []).filter(function (item) {
        return !isExcluded(item.product_title);
      });
      lastAddedProductId = items.length ? items[items.length - 1].product_id : null;
    }

    return lastAddedProductId;
  }

  function getRecommendations(productId) {
    var url =
      ROOT +
      '/recommendations/products.json?product_id=' +
      productId +
      '&limit=' + API_MAX +
      '&intent=related';
    return fetch(url, { credentials: 'same-origin' })
      .then(function (r) { return r.json(); })
      .then(function (data) { return data.products || []; });
  }

  function buildHTML(products, cart) {
    var cartIds = (cart.items || []).map(function (item) { return item.product_id; });

    var filtered = products
      .filter(function (p) {
        return (
          p.available &&
          cartIds.indexOf(p.id) === -1 &&
          !isExcluded(p.title) &&
          p.variants &&
          p.variants.length
        );
      })
      .slice(0, LIMIT);

    if (!filtered.length) return null;
    return filtered.map(recommendationHTML).join('');
  }

  /* ---------------------------------------------------------------- render */

  function containers() {
    // .drawer-red-bar (desktop) y .reco-mobile-list (mobile)
    return Array.prototype.slice.call(document.querySelectorAll('[data-reco-list]'));
  }

  function rememberFallback(container) {
    if (fallbackHTML.has(container)) return;
    var items = container.querySelectorAll('.reco-container');
    var html = '';
    for (var i = 0; i < items.length; i++) html += items[i].outerHTML;
    fallbackHTML.set(container, html);
  }

  /**
   * Placeholders con la misma estructura que una card, para que la barra tenga
   * el alto definitivo desde el principio y el contenido real no la haga saltar.
   */
  function skeletonHTML(count) {
    var one =
      '<div class="reco-container reco-skeleton" aria-hidden="true">' +
      '<div class="reco-img"><span class="reco-skeleton__box"></span></div>' +
      '<div class="reco-info">' +
      '<span class="reco-skeleton__line"></span>' +
      '<span class="reco-skeleton__line reco-skeleton__line--short"></span>' +
      '<span class="reco-skeleton__line reco-skeleton__line--price"></span>' +
      '</div>' +
      '</div>';
    return new Array(count + 1).join(one);
  }

  function reducedMotion() {
    return (
      window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches
    );
  }

  function replaceItems(container, html) {
    touched.add(container);
    var items = container.querySelectorAll('.reco-container');
    for (var i = items.length - 1; i >= 0; i--) items[i].remove();
    container.insertAdjacentHTML('beforeend', html);
  }

  /** Entrada escalonada de las cards nuevas. */
  function animateIn(container) {
    if (reducedMotion()) return;

    var items = container.querySelectorAll('.reco-container');
    for (var i = 0; i < items.length; i++) {
      items[i].classList.add('reco-container--enter');
      items[i].style.transitionDelay = Math.min(i, 6) * 40 + 'ms';
    }

    window.requestAnimationFrame(function () {
      window.requestAnimationFrame(function () {
        for (var i = 0; i < items.length; i++) {
          items[i].classList.remove('reco-container--enter');
          items[i].style.transitionDelay = '';
        }
      });
    });
  }

  /**
   * Cambia el contenido con un crossfade: primero se desvanece lo que hay, y
   * recién ahí se reemplaza, para que no salte de una lista a otra de golpe.
   */
  function swapItems(container, html) {
    if (!container.querySelector('.reco-container') || reducedMotion()) {
      replaceItems(container, html);
      animateIn(container);
      return;
    }

    container.classList.add('reco-list--leaving');
    window.setTimeout(function () {
      container.classList.remove('reco-list--leaving');
      replaceItems(container, html);
      animateIn(container);
    }, LEAVE_MS);
  }

  function paint(container) {
    rememberFallback(container);

    // Mientras se recalcula con contenido ya visible, se atenúa en vez de
    // vaciar: el usuario ve que algo está pasando y la barra no se mueve.
    container.classList.toggle('reco-list--busy', resolvedOnce && inFlight);

    // Antes de la primera respuesta van skeletons: mostrar los productos de la
    // colección sería enseñar los equivocados si las nativas los van a pisar.
    var token = resolvedOnce ? renderToken : 'skeleton';
    if (painted.get(container) === token) return;
    painted.set(container, token);

    if (!resolvedOnce) {
      var fallbackCount = (fallbackHTML.get(container) || '').split('reco-container').length - 1;
      replaceItems(container, skeletonHTML(Math.min(fallbackCount || LIMIT, LIMIT)));
      return;
    }

    // Fallback sobre markup de Liquid intacto: no hay nada que reemplazar.
    if (cache.html === null && !touched.has(container)) return;

    swapItems(
      container,
      cache.html === null ? fallbackHTML.get(container) || '' : cache.html
    );
  }

  function sync() {
    var list = containers();
    for (var i = 0; i < list.length; i++) paint(list[i]);
  }

  function update(cart) {
    // Si ya hay una consulta en curso se guarda el carrito mas reciente y se
    // reintenta al terminar, para no perder un cambio.
    if (inFlight) {
      pendingCart = cart || pendingCart || true;
      return;
    }

    inFlight = true;
    if (resolvedOnce) {
      containers().forEach(function (c) { c.classList.add('reco-list--busy'); });
    }

    Promise.resolve(cart || getCart())
      .then(function (data) {
        var productId = sourceProductId(data);

        if (!productId) {
          cache = { productId: null, html: null };
          return;
        }
        // Solo se re-piden recomendados cuando entra un producto nuevo al
        // carrito; agregar una bolsa no cambia el producto fuente.
        if (cache.productId === productId) {
          cache.html = buildHTML(cache.products || [], data);
          return;
        }

        return getRecommendations(productId).then(function (products) {
          cache = {
            productId: productId,
            products: products,
            html: buildHTML(products, data)
          };
        });
      })
      .catch(function () {
        // Cualquier fallo deja el listado de la coleccion (fallback).
        cache = { productId: null, html: null };
      })
      .then(function () {
        inFlight = false;
        resolvedOnce = true;
        renderToken++;
        sync();

        if (pendingCart) {
          var next = pendingCart;
          pendingCart = null;
          update(next === true ? null : next);
        }
      });
  }

  /* --------------------------------------------------------------- trigger */

  // Debounce con tope: agrupa las mutaciones de un re-render, pero garantiza
  // que se pinte dentro de SYNC_MAX_MS aunque las mutaciones no paren nunca.
  function scheduleSync() {
    var now = Date.now();
    if (!syncDeadline) syncDeadline = now + SYNC_MAX_MS;

    if (syncTimer) window.clearTimeout(syncTimer);
    syncTimer = window.setTimeout(function () {
      syncTimer = null;
      syncDeadline = 0;
      sync();
    }, Math.max(0, Math.min(SYNC_DEBOUNCE_MS, syncDeadline - now)));
  }

  function start() {
    var root = document.querySelector('.right-drawer-vue');
    if (!root) return;

    if (cfg.initialItems) prevCounts = productCounts(cfg.initialItems);

    // Red de seguridad: si la API no contesta a tiempo se revela igual el
    // listado de la colección. Nunca dejar la barra vacía.
    window.setTimeout(function () {
      if (resolvedOnce) return;
      resolvedOnce = true;
      renderToken++;
      sync();
    }, 2500);

    // El tema avisa cada cambio del carrito con este evento y adjunta el
    // carrito ya actualizado, asi que no hace falta volver a pedir /cart.js.
    document.documentElement.addEventListener('wetheme-cart-update', function (event) {
      var cart = event.detail && event.detail.cart;
      if (cart) update(cart);
    });

    // Vue re-renderiza el drawer al abrirlo y en cada cambio: hay que volver a
    // pintar los recomendados sobre los nodos nuevos.
    new MutationObserver(scheduleSync).observe(root, {
      childList: true,
      subtree: true
    });

    // Primera carga: el drawer se abre agregando una clase al body.
    new MutationObserver(function () {
      if (document.body.classList.contains('js-drawer-open-right')) {
        scheduleSync();
        if (cache.productId === null) update();
      }
    }).observe(document.body, { attributes: true, attributeFilter: ['class'] });

    scheduleSync();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
